import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Product Bundle Component
 * Manages adding multiple products to cart simultaneously
 */
class ProductBundle {
  constructor() {
    this.initializeButtons();
  }

  initializeButtons() {
    const buttons = document.querySelectorAll('[data-product-bundle-add-to-cart]');
    buttons.forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
  }

  /**
   * Handle add to cart button click
   * @param {Event} event - Click event
   */
  async handleAddToCart(event) {
    const button = event.currentTarget;
    const mainVariantId = button.dataset.mainVariantId;
    const bundleVariantId = button.dataset.bundleVariantId;
    const sectionId = button.closest('[data-section-id]')?.dataset.sectionId;

    if (!mainVariantId) {
      console.error('Main variant ID is required');
      return;
    }

    // Disable button and show loading state
    this.setLoadingState(button, true);

    try {
      // Get current product variant if user has selected different variant
      const currentVariantId = this.getCurrentSelectedVariant() || mainVariantId;
      
      // Prepare items to add
      const items = [
        {
          id: currentVariantId,
          quantity: 1
        }
      ];

      // Add bundle product if available
      if (bundleVariantId) {
        items.push({
          id: bundleVariantId,
          quantity: 1
        });
      }

      // Add to cart
      const response = await this.addItemsToCart(items);
      
      if (response.status) {
        // Handle error
        this.handleError(response.message, button);
        return;
      }

      // Success - handle post-add behavior
      this.handleSuccess(response, button, sectionId, items.length);

    } catch (error) {
      console.error('Add to cart error:', error);
      this.handleError('Erro ao adicionar produtos ao carrinho', button);
    } finally {
      this.setLoadingState(button, false);
    }
  }

  /**
   * Get currently selected variant from product form if exists
   * @returns {string|null} - Variant ID
   */
  getCurrentSelectedVariant() {
    const productForm = document.querySelector('product-form-component');
    if (!productForm) return null;

    const variantInput = productForm.querySelector('[name="id"]');
    return variantInput?.value || null;
  }

  /**
   * Add items to cart via AJAX
   * @param {Array} items - Items to add
   * @returns {Promise} - Response promise
   */
  async addItemsToCart(items) {
    const formData = new FormData();
    
    // Add items to form data
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);
    });

    // Add sections for cart updates
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((component) => {
      if (component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    });
    
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const config = fetchConfig('javascript', { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: {
        ...config.headers,
        Accept: 'application/json',
      },
    });

    return response.json();
  }

  /**
   * Set loading state on button
   * @param {HTMLElement} button - Button element
   * @param {boolean} loading - Loading state
   */
  setLoadingState(button, loading) {
    const textEl = button.querySelector('.product-bundle__cta-text');
    const loadingEl = button.querySelector('.product-bundle__cta-loading');

    if (loading) {
      button.classList.add('loading');
      button.disabled = true;
      textEl?.classList.add('hidden');
      loadingEl?.classList.remove('hidden');
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      textEl?.classList.remove('hidden');
      loadingEl?.classList.add('hidden');
    }
  }

  /**
   * Handle success response
   * @param {Object} response - Cart response
   * @param {HTMLElement} button - Button element
   * @param {string} sectionId - Section ID
   * @param {number} itemCount - Number of items added
   */
  handleSuccess(response, button, sectionId, itemCount) {
    // Dispatch cart update event
    const cartEvent = new CartAddEvent({}, sectionId, {
      source: 'product-bundle',
      itemCount: itemCount,
      sections: response.sections,
    });
    
    document.dispatchEvent(cartEvent);

    // Handle post-add behavior based on settings
    const section = button.closest('[data-section-id]');
    const cartAction = this.getCartAction(section);

    switch (cartAction) {
      case 'drawer':
        this.openCartDrawer();
        break;
      case 'redirect':
        window.location.href = '/cart';
        break;
      case 'toast':
        this.showToastMessage(`${itemCount} ${itemCount === 1 ? 'produto adicionado' : 'produtos adicionados'} ao carrinho`);
        break;
    }

    // Add success feedback to button
    this.showButtonFeedback(button, 'Adicionado!');
  }

  /**
   * Handle error response
   * @param {string} message - Error message
   * @param {HTMLElement} button - Button element
   */
  handleError(message, button) {
    this.showToastMessage(message, 'error');
    this.showButtonFeedback(button, 'Erro', 'error');
  }

  /**
   * Get cart action from section settings
   * @param {HTMLElement} section - Section element
   * @returns {string} - Cart action
   */
  getCartAction(section) {
    // Try to get cart action from data attribute or fallback to drawer
    const cartAction = section?.dataset?.cartAction;
    return cartAction || 'drawer';
  }

  /**
   * Open cart drawer
   */
  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

  /**
   * Show toast message
   * @param {string} message - Message to show
   * @param {string} type - Message type (success, error)
   */
  showToastMessage(message, type = 'success') {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `product-bundle-toast product-bundle-toast--${type}`;
    toast.textContent = message;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');

    // Add styles
    Object.assign(toast.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      zIndex: '9999',
      maxWidth: '300px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease',
      backgroundColor: type === 'error' ? '#dc3545' : '#28a745',
      color: '#ffffff',
    });

    document.body.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(0)';
    });

    // Remove after delay
    setTimeout(() => {
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }

  /**
   * Show button feedback
   * @param {HTMLElement} button - Button element
   * @param {string} message - Feedback message
   * @param {string} type - Feedback type
   */
  showButtonFeedback(button, message, type = 'success') {
    const textEl = button.querySelector('.product-bundle__cta-text');
    const originalText = textEl?.textContent;

    if (textEl) {
      textEl.textContent = message;
      button.classList.add(`feedback-${type}`);

      setTimeout(() => {
        textEl.textContent = originalText;
        button.classList.remove(`feedback-${type}`);
      }, 2000);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductBundle();
  });
} else {
  new ProductBundle();
}

// Handle theme editor updates
document.addEventListener('shopify:section:load', () => {
  new ProductBundle();
});

export default ProductBundle; 